import { keyframes, SxProps, Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';

/**
 * The guest-form visual language, in one place.
 *
 * Every block renderer draws from this vocabulary instead of choosing its own borders, radii and
 * weights, because the design language is a property of the form flow rather than of any one block —
 * a second opinion per block is how a catalog of ten types ends up looking like ten products.
 *
 * The language comes from the Signal Blocks prototypes: austere, hairlines instead of shadows, one
 * accent and no second one, tabular numerals on anything countable, selection carried by more than
 * colour. What those prototypes spell black, this spells `palette.primary` — the accent is the only
 * substitution, and it is why nothing here contains a literal colour (catalog §12.1, D19).
 *
 * The METRICS come from the mobile prototype specifically, and they are bigger than a desktop form
 * would choose: 60px rows, 20px markers, a 52px action button. That is not generosity, it is the
 * medium. This is answered one-handed on a phone, often outdoors and usually in a hurry, so every
 * target clears 44px — the ones that do not get mis-tapped, and a mis-tap on a form costs an answer.
 *
 * One discipline follows from swapping black for blue and it is not optional: **the accent means
 * "this is your answer"**. It is not available for emphasis, headings or decoration, because a
 * respondent who sees blue in two roles can no longer read one of them.
 */

/** 12px against the theme's 8px base — the mobile prototype's default radius. */
const RADIUS = 1.5;

/** The prototype's easing. Decisive out, soft in: it reads as the screen having arrived. */
export const SIGNAL_EASE = 'cubic-bezier(.22,.85,.24,1)';

/** Fine, dense numerals for keys, counters and ids. No webfont: the platform stack is enough. */
export const MONO_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

/**
 * Display type: the same Inter the app already self-hosts, pulled tight.
 *
 * The prototypes set headings in Inter Tight. Rather than ship a second family for one axis, the
 * tightening is applied as tracking — visually near-identical at these sizes, and it keeps the form
 * flow at zero additional font bytes on a connection we do not control.
 */
export const signalHeading: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: 23,
  letterSpacing: '-0.028em',
  lineHeight: 1.2
};

/** The quiet line under a question. Larger than a caption: it is meant to be read, not skimmed. */
export const signalHelp: SxProps<Theme> = {
  fontSize: 15,
  lineHeight: 1.5,
  color: 'text.secondary'
};

/**
 * The operator's line about HOW to answer, distinct from `help`, which says what the question means.
 *
 * pro-core gives the two separate fields, so the renderer keeps them separate: collapsing them loses
 * the distinction an operator was given a field to express. Smaller than help because it is the
 * second thing read, never the first.
 */
export const signalInstruction: SxProps<Theme> = {
  fontSize: 13,
  lineHeight: 1.5,
  color: 'text.secondary'
};

/**
 * The DERIVED line: what the control will and will not accept, read off the configuration.
 *
 * Last and quietest of the three, because it is the one a respondent needs only if the control did
 * not already tell them — and it is the only one that is not somebody's writing, so it must not
 * compete with the words that are.
 */
export const signalHint: SxProps<Theme> = {
  fontSize: 13,
  lineHeight: 1.5,
  color: 'text.disabled'
};

/** Read by a screen reader, invisible on the screen. */
export const signalVisuallyHidden: SystemStyleObject<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0
};

/** For counters and character budgets: digits that do not jitter as they change. */
export const signalMono: SxProps<Theme> = {
  fontFamily: MONO_FONT_FAMILY,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '0.01em'
};

// ── the shell ────────────────────────────────────────────────────────────────

/**
 * The frame: a full-height column of three regions — bar, scroll, actions.
 *
 * `100dvh` rather than `100vh` because mobile browsers count their collapsing address bar in `vh`,
 * which puts the action bar underneath it exactly when someone reaches for the button. The `100vh`
 * fallback is wrong by that toolbar's height, which is still better than no height at all.
 *
 * Only the middle region scrolls. That is what keeps the question's progress and the Next button in
 * place while the answer moves, and it is why the frame itself is `overflow: hidden`.
 */
export const signalAppFrame: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  '@supports (height: 100dvh)': { height: '100dvh' },
  overflow: 'hidden',
  backgroundColor: 'background.paper',
  position: 'relative',
  width: '100%'
};

/**
 * The measure, applied inside each region rather than to the frame.
 *
 * On a wide screen the frame still fills the viewport — it is a form, not a device, and drawing a
 * phone-shaped panel in the middle of a desktop would be a simulation rather than a layout. What
 * does not stretch is the LINE LENGTH: past roughly this width a question stops being comfortable to
 * read and an option row becomes a stripe with a word at each end.
 */
export const signalMeasure: SystemStyleObject<Theme> = {
  width: '100%',
  maxWidth: 560,
  mx: 'auto'
};

/** The fixed top region: title and progress. Never scrolls away. */
export const signalTopBar: SxProps<Theme> = {
  flex: 'none',
  px: 2.25,
  // Whatever the OS reserves for a notch or status bar, plus our own padding.
  pt: 'calc(env(safe-area-inset-top) + 12px)',
  backgroundColor: 'background.paper',
  borderBottom: '1px solid',
  borderColor: 'divider'
};

/**
 * The only scrolling region.
 *
 * `overscrollBehavior: contain` stops a flick at the end of a question from scrolling the page
 * behind it — on iOS that is the gesture that drags the browser chrome down over the action bar.
 */
// `SystemStyleObject` rather than `SxProps`, like the other composable pieces here: only the
// narrower type can be an element of an `sx={[...]}` array, and the runner composes this one with a
// conditional to freeze the region while a submit finalizes.
export const signalScrollArea: SystemStyleObject<Theme> = {
  flex: 1,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  // 22px / 18px / 26px, straight from the sample. Content starts at the top and stays there — an
  // earlier version centred it, which is a different composition and not this one.
  pt: '22px',
  px: '18px',
  pb: '26px'
};

/** The fixed bottom region. The primary action is always exactly here. */
export const signalActionBar: SxProps<Theme> = {
  flex: 'none',
  px: '18px',
  pt: '12px',
  // The home indicator on a gesture-navigation phone sits where a full-width button would be.
  pb: 'calc(env(safe-area-inset-bottom) + 14px)',
  backgroundColor: 'background.paper',
  borderTop: '1px solid',
  borderColor: 'divider'
};

/**
 * The primary action: full width, 52px, one per screen.
 *
 * Full width because the thumb arc reaches the bottom edge far more reliably than any particular
 * horizontal position — and because a form should only ever have one obvious next move.
 */
export const signalPrimaryAction: SxProps = {
  width: '100%',
  minHeight: 52,
  fontSize: 16,
  fontWeight: 500,
  borderRadius: RADIUS,
  transition: `background-color .15s, transform .1s ${SIGNAL_EASE}`,
  '&:active': { transform: 'scale(0.985)' },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:active': { transform: 'none' }
  }
};

// ── progress ─────────────────────────────────────────────────────────────────

/**
 * Segmented progress: one bar per question rather than one continuous fill.
 *
 * A continuous bar answers "how far along am I". Segments also answer "how many are left", which is
 * the question someone actually has on a form — three segments visibly means three screens, and that
 * is the difference between finishing and abandoning.
 */
export const signalProgressTrack: SystemStyleObject<Theme> = {
  display: 'flex',
  gap: '3px',
  pt: 1.5,
  pb: 1.25
};

export const signalProgressSegment = (
  state: 'done' | 'current' | 'todo'
): SxProps<Theme> => ({
  height: 3,
  flex: 1,
  borderRadius: '2px',
  transition: 'background-color .3s',
  // Current takes the accent because it is where the answer goes. Done is quiet: it is finished and
  // not worth the eye.
  backgroundColor: {
    current: 'primary.main',
    done: 'text.disabled',
    todo: 'divider'
  }[state],
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

// ── motion ───────────────────────────────────────────────────────────────────

const slideForward = keyframes`
  from { opacity: 0; transform: translate3d(16px, 0, 0); }
  to { opacity: 1; transform: none; }
`;

const slideBack = keyframes`
  from { opacity: 0; transform: translate3d(-16px, 0, 0); }
  to { opacity: 1; transform: none; }
`;

const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: none; }
`;

/**
 * A question enters from the direction you travelled.
 *
 * Ten pixels, not a page swipe: enough to say "this is a new screen and you moved forwards", short
 * enough that nobody waits for it. Without it, one question replacing another is indistinguishable
 * from the same question having its words changed — exactly the confusion a one-question-per-screen
 * flow has to avoid.
 *
 * Deliberately smaller and softer than a slide. The movement that actually reads as unsmooth is not
 * this one: it is the question BOX changing height between steps while the button stays put. Keeping
 * the travel short and the opacity doing most of the work stops the two movements competing.
 *
 * `translate3d` rather than `translateX` so the browser composites it on the GPU instead of
 * repainting a full-width block on every frame, which is where the judder came from on a phone.
 */
export const signalScreen = (
  direction: 'forward' | 'back'
): SystemStyleObject<Theme> => ({
  animation: `${
    direction === 'forward' ? slideForward : slideBack
  } .28s ${SIGNAL_EASE} both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
});

/** A panel that appears in response to a choice — the "tell us more" field. */
export const signalRevealed: SxProps<Theme> = {
  mt: 2,
  p: 2,
  borderRadius: RADIUS,
  backgroundColor: 'action.hover',
  animation: `${fadeDown} .3s ${SIGNAL_EASE} both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
};

// ── surfaces and focus ───────────────────────────────────────────────────────

/**
 * A hairline surface — the prototype's only container treatment.
 *
 * Elevation is left out on purpose. A shadow suggests the panel can be moved or dismissed; these
 * cannot, and once shadow carries no meaning it is texture competing with the answer.
 */
export const signalSurface: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: RADIUS
};

/**
 * The focus ring, stated once.
 *
 * Always visible on the keyboard route and never traded for a background tint: tapping is the
 * primary way through a form on a phone, but the keyboard is an equivalent route, not a fallback
 * (catalog §12).
 */
export const signalFocusRing: SxProps<Theme> = {
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2
  }
};

// ── choice rows ──────────────────────────────────────────────────────────────

/**
 * One selectable row — the shape every enumerated block reuses.
 *
 * Selection is a **border that changes colour**, not a filled tile. That is not a preference: a
 * tinted row and a pressed row read the same at arm's length in daylight, and the tint is what makes
 * a list of options look like a list of buttons. Weight and the marker carry the rest, so the state
 * survives greyscale and colour-blindness — colour is never the only signal.
 *
 * 60px tall with 16px padding, from the mobile prototype. Well past the 44px floor, because these
 * are stacked and adjacent targets are where mis-taps happen. The padding drops by a pixel when
 * selected so the thicker border does not shift the row's contents.
 *
 * `:active` leads and `:hover` is gated behind a pointer, because the primary device has neither a
 * cursor nor a hover state — and a sticky hover on touch is worse than none.
 *
 * `isEscape` draws the row dashed for an option that is not one of the enumerated answers —
 * "something else", "none of these". Such an option behaves differently from its neighbours:
 * choosing it asks a further question or clears the others, and the dash says so before it is tapped.
 */
export const signalOptionRow = (
  isSelected: boolean,
  isEscape = false
): SxProps<Theme> => ({
  ...signalFocusRing,
  width: '100%',
  display: 'flex',
  // Top-aligned, not centred: once a row carries a description the marker has to sit with the FIRST
  // line of the label rather than float halfway down a two-line block.
  alignItems: 'flex-start',
  gap: '13px',
  p: '15px',
  textAlign: 'left',
  borderRadius: RADIUS,
  backgroundColor: 'background.paper',
  border: '1px solid',
  borderStyle: isEscape ? 'dashed' : 'solid',
  // Only the COLOUR changes on selection — the border stays 1px. A 2px selected border has to be
  // paid for with a pixel of padding somewhere, and every row in the list shifts as you move down it.
  borderColor: isSelected ? 'primary.main' : 'divider',
  transition: 'border-color .16s, background-color .16s',
  '&:active': { backgroundColor: 'action.hover' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

/**
 * The marker: a 22px rounded square that fills when chosen.
 *
 * One shape for both single and multi select, which is what the sample does. Radio-versus-checkbox
 * is carried by the row's `role` and by whether choosing one clears the others, not by drawing two
 * different glyphs — and a filled square with a tick is legible at arm's length in a way a 10px dot
 * inside a ring is not.
 */
export const signalMarker = (isSelected: boolean): SxProps<Theme> => ({
  width: 22,
  height: 22,
  flex: 'none',
  mt: '1px',
  display: 'grid',
  placeItems: 'center',
  borderRadius: '6px',
  border: '1.5px solid',
  borderColor: isSelected ? 'primary.main' : 'divider',
  backgroundColor: isSelected ? 'primary.main' : 'transparent',
  color: isSelected ? 'primary.contrastText' : 'transparent',
  transition: 'background-color .15s, border-color .15s, color .15s',
  '& > svg': { width: 12, height: 12 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

/** Same marker for a multi select — see `signalMarker` for why there is only one. */
export const signalCheckMarker = signalMarker;

/** The label block inside a row. */
export const signalOptionLabel: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: 15,
  fontWeight: 500,
  lineHeight: 1.3
};

/** The quieter second line under an option's label. */
export const signalOptionDescription: SxProps<Theme> = {
  fontSize: 13,
  color: 'text.secondary',
  mt: '3px',
  lineHeight: 1.4
};

/** The row above a multi select: a running count on the left, bulk actions on the right. */
export const signalSelectionBar: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  mb: 1.5
};

// ── segmented tray ───────────────────────────────────────────────────────────

/**
 * A row of equal options inside one inset tray — the prototype's `.opts4`.
 *
 * For options that are short and comparable: a rating scale, a yes/no. The tray is what makes them
 * read as ONE instrument with a position rather than as several buttons that happen to be adjacent.
 * Stacked rows say "here is a list"; a tray says "here is a range".
 */
export const signalTray: SxProps<Theme> = {
  display: 'flex',
  gap: '4px',
  p: '4px',
  borderRadius: RADIUS,
  backgroundColor: 'action.hover'
};

/**
 * One segment.
 *
 * Selected lifts onto the paper surface with a soft shadow — the one place a shadow is used in this
 * vocabulary, and it earns it: raised-out-of-the-tray is the whole affordance, and a tint alone
 * would not say it.
 */
export const signalTraySegment = (isSelected: boolean): SxProps<Theme> => ({
  ...signalFocusRing,
  flex: 1,
  minWidth: 0,
  minHeight: 44,
  px: 0.75,
  py: 1,
  borderRadius: '8px',
  display: 'grid',
  placeItems: 'center',
  lineHeight: 1.25,
  color: isSelected ? 'primary.main' : 'text.secondary',
  fontWeight: isSelected ? 500 : 400,
  backgroundColor: isSelected ? 'background.paper' : 'transparent',
  boxShadow: isSelected
    ? '0 1px 2px rgba(17,17,19,.05), 0 1px 3px rgba(17,17,19,.04)'
    : 'none',
  transition: 'background-color .15s, color .15s, box-shadow .15s',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

// ── the number dial ──────────────────────────────────────────────────────────

/**
 * The value itself: large, tight, tabular.
 *
 * 52px, deliberately headline-sized. The number IS the answer on this screen, and the prototype
 * makes it the largest thing on it — larger than the question. Tabular numerals matter more here
 * than anywhere else in the flow: this digit changes under the respondent's thumb, and proportional
 * figures would shift the line on every tap.
 */
export const signalDialValue: SxProps<Theme> = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: 52,
  fontWeight: 600,
  letterSpacing: '-0.04em',
  lineHeight: 1
};

/**
 * The adjust buttons: pills, side by side, splitting the width between them.
 *
 * Pills rather than small round steppers, because a 44px-tall target spanning half the screen can be
 * hit without looking — the difference between adjusting a number while walking and stopping to aim
 * at a circle.
 */
export const signalNudgeButton: SxProps<Theme> = {
  ...signalFocusRing,
  flex: 1,
  minHeight: 44,
  borderRadius: 999,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  fontSize: 14,
  '&:active': { backgroundColor: 'action.hover', color: 'text.primary' },
  '&.Mui-disabled': { opacity: 0.35 }
};

/**
 * The dial's slider, brought into this vocabulary.
 *
 * MUI's default is a desktop control: a 20px thumb on a 4px rail, sized for a cursor that can be
 * placed exactly. This flow's floor is 44px, so the thumb grows to 28 and the ROOT reserves the
 * full 44 — a slider is dragged with a thumb that covers the control it is moving, and the extra
 * height is what makes the first touch land on the handle instead of jumping the value.
 *
 * The accent appears on the thumb and the filled track only, which is the same rule every other
 * control here follows: **blue means "this is your answer"**. The rail stays a hairline, so an
 * untouched dial reads as untouched rather than as one sitting at its minimum.
 */
export const signalSlider: SxProps<Theme> = {
  color: 'primary.main',
  height: 6,
  py: '19px',
  '& .MuiSlider-rail': {
    opacity: 1,
    backgroundColor: 'divider'
  },
  '& .MuiSlider-track': {
    border: 'none'
  },
  '& .MuiSlider-thumb': {
    width: 28,
    height: 28,
    backgroundColor: 'background.paper',
    border: '2px solid',
    borderColor: 'primary.main',
    transition: `box-shadow .15s ${SIGNAL_EASE}`,
    // MUI's default halo is a tinted disc that reads as a second selection state; a focus ring is
    // the treatment every other control in this flow uses, so the slider uses it too.
    '&::before': { boxShadow: 'none' },
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 0 6px rgba(17,17,19,.06)'
    },
    '&.Mui-active': {
      boxShadow: '0 0 0 10px rgba(17,17,19,.08)'
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
  }
};

// ── fields, meters and notes ─────────────────────────────────────────────────

/**
 * A text input in the same language: hairline at rest, accent when focused, no filled variant.
 *
 * 16px on the input itself is not a typographic choice: iOS zooms the viewport on focus for anything
 * smaller, and a form that jumps when you tap a field feels broken.
 */
export const signalTextField: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    borderRadius: RADIUS,
    backgroundColor: 'background.paper',
    fontSize: 16,
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'text.disabled' },
    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' }
  }
};

/** An inset panel holding a running total or a limit — the prototype's `.meter`. */
export const signalMeterPanel: SxProps<Theme> = {
  mt: 2.25,
  p: '15px 16px',
  borderRadius: RADIUS,
  backgroundColor: 'action.hover'
};

/**
 * A note about the state of an answer: neutral, positive or cautionary.
 *
 * Colour here is semantic, and this is the only place a second hue appears in the flow. It never
 * means "selected" — that is the accent's job and the accent's only job.
 */
export const signalNote = (
  tone: 'neutral' | 'positive' | 'caution'
): SxProps<Theme> => ({
  mt: 1.75,
  p: '13px 14px',
  borderRadius: RADIUS,
  fontSize: 14,
  lineHeight: 1.45,
  ...{
    neutral: { backgroundColor: 'action.hover', color: 'text.secondary' },
    positive: { backgroundColor: 'success.lighter', color: 'success.darker' },
    caution: { backgroundColor: 'warning.lighter', color: 'warning.darker' }
  }[tone]
});

/**
 * A small monospaced pill for a running count.
 *
 * `isSatisfied` turns it positive, and that is the one semantic use of colour on a counter: "you
 * have reached what we suggested". It never appears for a hard limit — a limit is not an achievement.
 */
export const signalCountPill = (isSatisfied: boolean): SxProps<Theme> => ({
  ...signalMono,
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  px: '12px',
  py: '6px',
  borderRadius: 999,
  fontSize: 11.5,
  lineHeight: 1,
  backgroundColor: isSatisfied ? 'success.lighter' : 'action.hover',
  color: isSatisfied ? 'success.darker' : 'text.secondary',
  transition: 'background-color .16s, color .16s',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

/**
 * A horizontal scale cell, for a scale too long for a tray's segments to stay readable.
 */
export const signalScaleCell = (isSelected: boolean): SxProps<Theme> => ({
  ...signalFocusRing,
  flex: 1,
  minWidth: 0,
  minHeight: 60,
  display: 'grid',
  placeItems: 'center',
  borderRadius: RADIUS,
  border: '1px solid',
  borderColor: isSelected ? 'primary.main' : 'divider',
  backgroundColor: 'background.paper',
  color: isSelected ? 'primary.main' : 'text.secondary',
  fontVariantNumeric: 'tabular-nums',
  transition: 'border-color .16s, color .16s',
  '&:active': { backgroundColor: 'action.hover' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

/**
 * One cell of the availability grid.
 *
 * A near-sibling of `signalScaleCell` and deliberately its own token rather than an inline override
 * of it: a grid cell sits in a `td` where `flex` means nothing, it is one of dozens rather than one
 * of five, and it is 52px because six rows of 60 leave the last slot below the fold on a small
 * phone. Encoding that here rather than in the block is what keeps the difference a decision of the
 * vocabulary instead of one renderer's opinion.
 *
 * Everything else matches the scale cell exactly — hairline at rest, accent border when chosen, and
 * a tick inside so the state survives greyscale.
 */
export const signalGridCell = (isSelected: boolean): SxProps<Theme> => ({
  ...signalFocusRing,
  width: '100%',
  minHeight: 52,
  display: 'grid',
  placeItems: 'center',
  borderRadius: RADIUS,
  border: '1px solid',
  borderColor: isSelected ? 'primary.main' : 'divider',
  backgroundColor: 'background.paper',
  color: isSelected ? 'primary.main' : 'text.secondary',
  transition: 'border-color .16s, color .16s',
  '&:active': { backgroundColor: 'action.hover' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

/** A column header in the availability grid — quiet, because the cells are the answer. */
export const signalGridHeader: SxProps<Theme> = {
  fontSize: 13,
  fontWeight: 500,
  color: 'text.secondary',
  minWidth: 64
};

/**
 * A row header in the availability grid — the slot's name.
 *
 * Louder than the column header, because a column is read once across the top while a row label is
 * what a respondent scans down the left to find the shift they mean.
 */
export const signalGridRowLabel: SxProps<Theme> = {
  fontSize: 14,
  fontWeight: 500,
  color: 'text.primary',
  lineHeight: 1.3
};

// ── calendar ─────────────────────────────────────────────────────────────────

/**
 * One day in the month grid.
 *
 * Selected fills rather than outlines, which is the one place in this vocabulary a fill means
 * "chosen" — a 40px square is too small to carry a 2px border and a legible number at the same time,
 * and an outlined day next to 34 others reads as noise.
 *
 * Unselectable days are dimmed rather than removed — a gap where the 14th should be reads as a
 * rendering fault, a dimmed 14th reads as a rule. That state comes from the button's own `disabled`
 * prop via `.Mui-disabled` rather than a second argument here, so "cannot be picked" has exactly one
 * source: whether the control is actually disabled.
 */
export const signalDayCell = (isSelected: boolean): SxProps<Theme> => ({
  ...signalFocusRing,
  aspectRatio: '1',
  width: '100%',
  borderRadius: '10px',
  fontSize: 14,
  fontVariantNumeric: 'tabular-nums',
  color: isSelected ? 'primary.contrastText' : 'text.primary',
  fontWeight: isSelected ? 500 : 400,
  backgroundColor: isSelected ? 'primary.main' : 'transparent',
  '&:active': { backgroundColor: isSelected ? 'primary.dark' : 'action.hover' },
  // Struck through as well as dimmed, which is the sample's treatment: at a glance a line through a
  // date says "not this one" faster than a lighter grey does, and it survives a bright screen
  // outdoors where the contrast difference alone would not.
  '&.Mui-disabled': {
    color: 'text.disabled',
    opacity: 0.4,
    textDecoration: 'line-through'
  }
});

/**
 * Paging, one step at a time: the calendar's months and the item-scale deck's cards.
 *
 * Shared rather than copied because it is the same job — "there is more of this in that direction" —
 * and two subtly different arrows in one flow would read as two different mechanisms. Disabled
 * rather than hidden at either end, so the control stays where the thumb left it.
 */
export const signalPagingArrow: SxProps<Theme> = {
  ...signalFocusRing,
  width: 38,
  height: 38,
  borderRadius: '10px',
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  '&:active': { backgroundColor: 'action.hover' },
  '&.Mui-disabled': { opacity: 0.3 }
};

/**
 * The calendar's header: month name on the left, both arrows together on the right.
 *
 * Grouped rather than split to either edge. Paging is one job done twice, and a thumb that has just
 * pressed "back" is already over "forward" — splitting them across the width makes every second
 * press a journey.
 */
export const signalCalendarBar: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.25,
  mb: 1.25
};
